/**
 * AssociationDossierManager — validation du dossier d'une association (staff GRC/mairie).
 *
 * Affiché dans un Dialog ouvert depuis la fiche. Permet de :
 *   - voir les pièces déposées (ouvrir le PDF, cocher conforme) ;
 *   - voir les pièces demandées (manquantes vs déposées) ;
 *   - valider ou refuser le dossier (avec motif).
 */

import { useEffect, useState } from 'react';
import { Check, X, FileText, ExternalLink } from 'lucide-react';
import { api, ApiException } from '@/lib/api';
import { Button, Field, Input, Alert, Badge } from '@/components/ui';
import { useToast } from '@/components/ui/Toast';
import type { AssociationDossierDetail } from '@roronoa-collectivites/shared-types';

function statutBadge(s: string) {
  if (s === 'valide') return <Badge variant="success">Validé</Badge>;
  if (s === 'soumis') return <Badge variant="warning">Soumis — à instruire</Badge>;
  if (s === 'refuse') return <Badge variant="urgent">Refusé</Badge>;
  return <Badge variant="info">Brouillon</Badge>;
}

export function AssociationDossierManager({ associationId }: { associationId: string }) {
  const { toast } = useToast();
  const [detail, setDetail] = useState<AssociationDossierDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [motif, setMotif] = useState('');
  const [busy, setBusy] = useState(false);

  async function charger() {
    setLoading(true); setError(null);
    try { setDetail(await api.associations.dossiers.getDossier(associationId)); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur de chargement'); }
    finally { setLoading(false); }
  }
  useEffect(() => { charger(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [associationId]);

  async function ouvrirPiece(pieceId: string) {
    try { const { url } = await api.associations.dossiers.pieceUrl(pieceId); window.open(url, '_blank', 'noopener'); }
    catch (e) { toast.error(e instanceof ApiException ? e.message : 'Erreur'); }
  }
  async function toggleConforme(pieceId: string, conforme: boolean) {
    try { await api.associations.dossiers.cocherPiece(pieceId, conforme); await charger(); }
    catch (e) { toast.error(e instanceof ApiException ? e.message : 'Erreur'); }
  }
  async function statuer(decision: 'valide' | 'refuse') {
    if (!detail) return;
    if (decision === 'refuse' && !motif.trim()) { setError('Indiquez un motif de refus.'); return; }
    setBusy(true); setError(null);
    try {
      await api.associations.dossiers.valider(detail.id, decision, decision === 'refuse' ? motif.trim() : undefined);
      toast.success(decision === 'valide' ? 'Dossier validé' : 'Dossier refusé');
      await charger();
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
    finally { setBusy(false); }
  }

  if (loading) return <div className="py-6 text-center text-sm text-ink-subtle">Chargement…</div>;
  if (!detail) return <Alert variant="error">{error ?? 'Dossier introuvable'}</Alert>;

  const deposeesParPiece = new Map(detail.piecesDeposees.filter((p) => p.pieceDemandeeId).map((p) => [p.pieceDemandeeId!, p]));

  return (
    <div className="space-y-4">
      {error && <Alert variant="error">{error}</Alert>}
      <div className="flex items-center gap-2 text-sm">
        <span className="text-ink-muted">Statut du dossier :</span> {statutBadge(detail.statut)}
      </div>
      {detail.motifRefus && <Alert variant="warning">Motif de refus précédent : {detail.motifRefus}</Alert>}

      {/* Pièces demandées + état */}
      <div>
        <div className="mb-2 text-sm font-medium text-ink">Pièces demandées</div>
        <div className="space-y-2">
          {detail.piecesDemandees.map((pd) => {
            const dep = deposeesParPiece.get(pd.id);
            return (
              <div key={pd.id} className="flex items-center justify-between gap-2 rounded-r-md border border-border bg-surface/40 p-2.5">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-sm text-ink">
                    {pd.libelle}
                    {pd.obligatoire && <Badge variant="info">obligatoire</Badge>}
                  </div>
                  {dep ? (
                    <div className="mt-0.5 flex items-center gap-1 text-xs text-ink-subtle">
                      <FileText className="h-3 w-3" /> {dep.nomFichier}
                    </div>
                  ) : (
                    <div className="mt-0.5 text-xs text-urgent">Non déposée</div>
                  )}
                </div>
                {dep && (
                  <div className="flex shrink-0 items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => ouvrirPiece(dep.id)}><ExternalLink className="h-3.5 w-3.5" /> Ouvrir</Button>
                    <Button variant="ghost" size="sm"
                      onClick={() => toggleConforme(dep.id, !(dep.conforme === true))}
                      className={dep.conforme === true ? 'text-success hover:bg-success/10 hover:text-success' : 'text-ink-muted'}>
                      <Check className="h-3.5 w-3.5" /> {dep.conforme === true ? 'Conforme' : 'Marquer conforme'}
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Autres pièces (hors liste) déposées */}
      {detail.piecesDeposees.filter((p) => !p.pieceDemandeeId).length > 0 && (
        <div>
          <div className="mb-2 text-sm font-medium text-ink">Autres pièces déposées</div>
          <div className="space-y-2">
            {detail.piecesDeposees.filter((p) => !p.pieceDemandeeId).map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-2 rounded-r-md border border-border bg-surface/40 p-2.5">
                <div className="flex items-center gap-1 text-xs text-ink-subtle"><FileText className="h-3 w-3" /> {p.nomFichier}</div>
                <Button variant="ghost" size="sm" onClick={() => ouvrirPiece(p.id)}><ExternalLink className="h-3.5 w-3.5" /> Ouvrir</Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Décision (uniquement si soumis) */}
      {detail.statut === 'soumis' && (
        <div className="space-y-2 border-t border-border pt-3">
          <Field label="Motif (en cas de refus)" htmlFor="dossier-motif">
            <Input id="dossier-motif" value={motif} onChange={(e) => setMotif(e.target.value)} placeholder="Pièce non conforme, document manquant…" />
          </Field>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => statuer('refuse')} disabled={busy} className="text-urgent hover:bg-urgent/10 hover:text-urgent">
              <X className="h-4 w-4" /> Refuser
            </Button>
            <Button variant="asso" onClick={() => statuer('valide')} disabled={busy}>
              <Check className="h-4 w-4" /> Valider le dossier
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
